import type { ProjectSettingsApiInfo, RawProjectSettingsApiResponse } from '../model/Project';
import { getProjectKeyFromHeaders } from './projectKey';
import { getRegionFromHeaders } from './region';
import { reportError, reportWarning } from './reportError';
import { isProjectHost } from './subdomain';

export class ProjectSettingsError extends Error {
  name = 'ProjectSettingsError';
}

export async function configureProject(headers: Headers): Promise<{
  projectKey: string | undefined;
  projectSettings: RawProjectSettingsApiResponse | undefined;
}> {
  const projectKey = getProjectKeyFromHeaders(headers);
  const region = getRegionFromHeaders(headers);

  const projectServiceUrlVarName = 'NEXT_PUBLIC_RMT_PROJECT_SERVICE_URL';
  const projectServiceBaseUrl = process.env[projectServiceUrlVarName];

  if (projectKey) {
    const projectSettings = await tryToGetProjectSettings(
      region,
      projectServiceBaseUrl,
      projectKey,
    );
    if (!projectSettings) {
      console.warn(`Couldn't get project settings for project key '${projectKey}'`);
      clearCachedProjectSettings(projectKey, region);
    }

    return { projectKey, projectSettings };
  }

  // If we're not at the app subdomain, throw an exception — project key is required
  if (isProjectHost(headers.get('x-forwarded-host') ?? undefined)) {
    throw new Error('Project key not set');
  }

  return { projectKey: undefined, projectSettings: undefined };
}

/**
 * Project settings for a server-rendered page that cannot work without them,
 * including the API location. Throws instead of handing the page an undefined it
 * would dereference a few lines later.
 */
export async function requireProjectSettings(
  headers: Headers,
): Promise<RawProjectSettingsApiResponse & { api: ProjectSettingsApiInfo }> {
  const { projectKey, projectSettings } = await configureProject(headers);
  if (!projectSettings?.api) {
    throw new ProjectSettingsError(`Project settings unavailable for project key '${projectKey}'`);
  }

  return { ...projectSettings, api: projectSettings.api };
}

const regions = ['eu', 'us-east'];

const projectSettingsRequestCache: any = {};

const projectSettingsCache: any = {};

export async function tryToGetProjectSettings(
  region: string | undefined,
  projectServiceBaseUrl: string | undefined,
  projectKey: string,
) {
  if (projectServiceBaseUrl) {
    const url = `${projectServiceBaseUrl}/projects/${projectKey}/settings`;

    try {
      const res = await fetchProjectSettings(url);
      return parseProjectSettingsResponse(res);
    } catch (e) {
      reportWarning('[projectSettings] base url fetch failed', e, { url, projectKey });
    }
  }

  if (region) {
    try {
      const url = `https://api.${region}.retailmediatools.com/v1/projects/${projectKey}/settings`;
      const res = await fetchProjectSettings(url);
      projectSettingsRequestCache[url] = res;

      const data = await parseProjectSettingsResponse(res);
      if (data) {
        return Promise.resolve(data);
      }
    } catch (e) {
      reportWarning('[projectSettings] regional fetch failed', e, { region, projectKey });
    }
  }

  try {
    console.warn(
      `Need to search for the project in ALL regions (, region=${region}, projectKey=${projectKey}, projectServiceBaseUrl=${projectServiceBaseUrl})`,
    );

    const tryRegions = region ? regions.filter((r) => r !== region) : regions;
    const urls = tryRegions.map(
      (region) => `https://api.${region}.retailmediatools.com/v1/projects/${projectKey}/settings`,
    );

    const fetchers = urls.map((url) => fetchProjectSettings(url));
    const responses = await Promise.all(fetchers);

    if (responses.length !== urls.length) {
      throw new Error('Precondition failed: unknown error');
    }

    for (let i = 0; i < responses.length; i++) {
      const url = urls[i];
      const res = responses[i];
      projectSettingsRequestCache[url] = res;
      const data = await parseProjectSettingsResponse(res);

      if (data) {
        return Promise.resolve(data);
      }
    }
  } catch (e) {
    reportError('[projectSettings] cross-region fallback failed', e, { region, projectKey });
  }

  reportWarning(
    '[projectSettings] could not resolve project settings',
    new Error(`Project '${projectKey}' not found via base url or any region`),
    { projectKey, region, projectServiceBaseUrl },
  );
  clearCachedProjectSettings(projectKey, region);
  return Promise.resolve(undefined);
}

/**
 * Drop any cached project-settings entries for a project key so the next
 * request retries the upstream lookup instead of serving stale data. Called
 * when every fetch path fails — keeping a stale entry around would mask a
 * recovered upstream and cause inconsistent renders.
 */
export function clearCachedProjectSettings(projectKey: string, region?: string) {
  for (const url of Object.keys(projectSettingsCache)) {
    if (url.includes(`/projects/${projectKey}/`)) {
      delete projectSettingsCache[url];
    }
  }
  for (const url of Object.keys(projectSettingsRequestCache)) {
    if (url.includes(`/projects/${projectKey}/`)) {
      delete projectSettingsRequestCache[url];
    }
  }
  if (region) {
    if (baseUrlCache[region]?.[projectKey]) {
      delete baseUrlCache[region][projectKey];
    }
  } else {
    for (const r of Object.keys(baseUrlCache)) {
      if (baseUrlCache[r]?.[projectKey]) {
        delete baseUrlCache[r][projectKey];
      }
    }
  }
}

async function fetchProjectSettings(url: string) {
  // #FIXME: Need cache invalidation strategy
  // if (projectSettingsRequestCache[url]) {
  //   console.info(`Using cached project settings response for ${url}`);
  //   return Promise.resolve(projectSettingsRequestCache[url]);
  // }

  try {
    // console.debug('Fetching project settings');
    return fetch(url);
  } catch (e) {
    reportError('[projectSettings] fetch threw', e, { url });
    throw e;
  }
}

async function parseProjectSettingsResponse(res: Response) {
  if (res.ok && res.status === 200) {
    try {
      // #FIXME: Need cache invalidation strategy
      // const { url } = res;
      // if (projectSettingsCache[url]) {
      //   console.info(`Using cached project settings for ${url}`);
      //   return Promise.resolve(projectSettingsCache[url]);
      // }

      const data = await res.json();
      if (data?.data) {
        projectSettingsCache[res.url] = data.data;

        return Promise.resolve(data.data);
      }

      console.warn('Invalid project settings response', data);
    } catch (e) {
      reportError('[projectSettings] response parse failed', e, { url: res.url });
    }
  }

  console.warn('Invalid project settings response', res);
  return Promise.resolve(undefined);
}

// Entries are { url, expiresAt }. The TTL bounds how long a resolved base URL is
// trusted, so a stale (or wrong) entry self-heals on the next request instead of
// surviving until the process restarts. See clearCachedProjectSettings for the
// failure-path eviction.
const BASE_URL_CACHE_TTL_MS = 60 * 1000;
const baseUrlCache: Record<string, Record<string, { url: string; expiresAt: number }>> = {};

export function getCachedBaseUrl(headers: Headers) {
  const projectKey = getProjectKeyFromHeaders(headers);
  const region = getRegionFromHeaders(headers);

  if (!projectKey || !region) return undefined;

  const entry = baseUrlCache[region]?.[projectKey];
  if (!entry) return undefined;

  if (entry.expiresAt <= Date.now()) {
    delete baseUrlCache[region][projectKey];
    return undefined;
  }

  return entry.url;
}

export function setCachedBaseUrl(headers: Headers, url: string) {
  const projectKey = getProjectKeyFromHeaders(headers);
  const region = getRegionFromHeaders(headers);

  if (!projectKey || !region) return;

  if (!baseUrlCache.hasOwnProperty(region)) {
    baseUrlCache[region] = {};
  }

  baseUrlCache[region][projectKey] = { url, expiresAt: Date.now() + BASE_URL_CACHE_TTL_MS };
}
